import { ComponentProps, FC, ReactNode, useRef } from "react";

import {
  Column,
  Row,
  Box,
  Popover,
  PopoverTrigger,
  Portal,
  Text,
  LogoutIcon,
} from "@hightouch/htui";

import { Link } from "src/router";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
} from "src/components/layout/nav/nav-popovers";

export const UserMenu: FC<
  Readonly<{
    children: ReactNode;
    user: { name: string; email: string } | null;
    placement?: ComponentProps<typeof Popover>["placement"];
    offset?: ComponentProps<typeof Popover>["offset"];
    // Optional controlled open state; omit for uncontrolled behavior.
    isOpen?: boolean;
    onClose?: () => void;
  }>
> = ({ children, user, placement = "bottom-end", offset, isOpen, onClose }) => {
  const initialRef = useRef<HTMLAnchorElement>(null);
  return (
    <Popover
      isLazy
      initialFocusRef={initialRef}
      placement={placement}
      offset={offset}
      isOpen={isOpen}
      onClose={onClose}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <NavPopoverContentAndBody>
          <Column
            borderBottomWidth="hairline"
            borderBottomStyle="solid"
            borderColor="base.border"
            gap={2}
            p={2}
            pb={3}
          >
            <Row gap={3} mx={1.5} justify="space-between" overflow="hidden">
              <Box overflow="hidden">
                <Box
                  fontSize="lg"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {user?.name}
                </Box>
                <Box
                  color="text.tertiary"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {user?.email}
                </Box>
              </Box>
            </Row>

            <Row align="center" color="text.tertiary" gap={2} mx={1.5}>
              <Link
                isExternal
                ref={initialRef}
                href={`${import.meta.env.VITE_WEBSITE_URL}/terms-of-service`}
                _focus={{ boxShadow: "none", outline: "none" }}
                _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
              >
                Terms
              </Link>
              •
              <Link
                isExternal
                href={`${import.meta.env.VITE_WEBSITE_URL}/privacy-policy`}
                _focus={{ boxShadow: "none", outline: "none" }}
                _focusVisible={{ boxShadow: "outline", borderRadius: "sm" }}
              >
                Privacy
              </Link>
            </Row>
          </Column>
          <Row
            {...NAV_POPOVER_MENU_ITEM_STYLES}
            as="button"
            mt={2}
            gap={2}
            align="center"
            color="text.tertiary"
            _hover={{ bg: "base.background", color: "text.secondary" }}
            _focus={{ outline: "none" }}
            _active={{ bg: "primary.background", color: "primary.base" }}
            _focusVisible={{ bg: "primary.background", boxShadow: "outline" }}
            onClick={() => {
              window.location.href = `${
                import.meta.env.VITE_API_BASE_URL
              }/auth/logout`;
            }}
          >
            <Box as={LogoutIcon} fontSize="20px" />
            <Text fontWeight="medium">Log out of Hightouch</Text>
          </Row>
        </NavPopoverContentAndBody>
      </Portal>
    </Popover>
  );
};
